import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { RefreshCw, Sparkles, CheckCircle } from 'lucide-react';
import { useState } from 'react';

interface SecurityTaskDialogProps {
  open: boolean;
  onClose: () => void;
  findingId: string;
  findingText: string;
  category: string;
  appId: string;
  onSubmit: (data: {
    findingId: string;
    appId: string;
    task: string;
    taskType: 'fix' | 'reanalyse' | 'mark-resolved';
    resolvedJustification?: string;
  }) => Promise<void>;
}

export function SecurityTaskDialog({
  open,
  onClose,
  findingId,
  findingText,
  category,
  appId,
  onSubmit,
}: SecurityTaskDialogProps) {
  const theme = useTheme();
  const [taskType, setTaskType] = useState<'fix' | 'reanalyse' | 'mark-resolved'>('fix');
  const [task, setTask] = useState('');
  const [resolvedJustification, setResolvedJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (taskType === 'mark-resolved') {
      if (!resolvedJustification.trim() || resolvedJustification.trim().length < 20) {
        setError('Please provide a justification (minimum 20 characters)');
        return;
      }
    } else if (taskType === 'fix' && !task.trim()) {
      setError('Please enter a task description');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit({
        findingId,
        appId,
        task: task.trim(),
        taskType,
        ...(taskType === 'mark-resolved' && {
          resolvedJustification: resolvedJustification.trim(),
        }),
      });

      // Reset form
      setTask('');
      setResolvedJustification('');
      setTaskType('fix');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setTask('');
      setResolvedJustification('');
      setTaskType('fix');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Sparkles size={20} color={theme.palette.primary.main} />
          <Typography variant="h6" component="span">
            Assign Security Task
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <strong>Category:</strong> {category}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          <strong>Finding:</strong> {findingText}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Task Type Selection */}
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
              Task Type
            </FormLabel>
            <RadioGroup
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as 'fix' | 'reanalyse' | 'mark-resolved')}
            >
              <FormControlLabel
                value="fix"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Fix Issue
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      AI agent will analyze and fix the security issue, create a branch and PR
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="reanalyse"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Reanalyse Finding
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Re-run the security analysis for this specific finding only
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="mark-resolved"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Mark as Resolved
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Manually mark this finding as resolved (requires justification)
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* Mark Resolved Fields */}
          {taskType === 'mark-resolved' && (
            <TextField
              label="Justification (Required)"
              multiline
              rows={5}
              fullWidth
              value={resolvedJustification}
              onChange={(e) => setResolvedJustification(e.target.value)}
              placeholder="Explain why this finding is being marked as resolved (minimum 20 characters)..."
              helperText={`${resolvedJustification.length} / 20 minimum characters. This will be recorded in the audit trail.`}
              error={!!error && (!resolvedJustification.trim() || resolvedJustification.trim().length < 20)}
              required
            />
          )}

          {/* Task Description for Fix */}
          {taskType === 'fix' && (
            <TextField
              label="Task Description"
              multiline
              rows={6}
              fullWidth
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder='E.g., "Add input validation annotations to all controller methods" or "Implement proper authentication checks for the API endpoints"'
              helperText="Describe the fix you want the AI agent to implement. The agent will make code changes and create a pull request."
              error={!!error && !task.trim()}
            />
          )}

          {/* Reanalyse Instructions */}
          {taskType === 'reanalyse' && (
            <TextField
              label="Reanalysis Instructions (Optional)"
              multiline
              rows={4}
              fullWidth
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder='E.g., "Focus on the authentication module" or "Check if the recent security patch addresses this issue"'
              helperText="Optionally provide specific instructions for the reanalysis."
            />
          )}

          {/* Info Alert */}
          <Alert
            severity="info"
            icon={
              taskType === 'fix' ? (
                <Sparkles size={18} />
              ) : taskType === 'reanalyse' ? (
                <RefreshCw size={18} />
              ) : (
                <CheckCircle size={18} />
              )
            }
            sx={{ bgcolor: alpha(theme.palette.info.main, 0.08) }}
          >
            <Typography variant="body2">
              {taskType === 'fix' ? (
                <>
                  <strong>Fix Issue:</strong> The AI agent will analyze the codebase, implement the
                  necessary security fix, create a git branch, commit changes, and open a pull request
                  for review.
                </>
              ) : taskType === 'reanalyse' ? (
                <>
                  <strong>Reanalyse:</strong> The AI will re-run the security analysis for this
                  specific finding only. This is useful if code has changed or you want a fresh
                  assessment.
                </>
              ) : (
                <>
                  <strong>Mark as Resolved:</strong> Manually mark this finding as resolved. The
                  justification will be recorded for audit purposes. Use this when the issue has been
                  fixed externally or is a false positive.
                </>
              )}
            </Typography>
          </Alert>

          {/* Error Message */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Task Details */}
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.grey[500], 0.05),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Task Details:
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                <strong>Finding ID:</strong> {findingId}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                <strong>Category:</strong> {category}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                <strong>App ID:</strong> {appId}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting ||
            (taskType === 'mark-resolved'
              ? !resolvedJustification.trim() || resolvedJustification.trim().length < 20
              : taskType === 'fix' && !task.trim())
          }
          startIcon={submitting ? <CircularProgress size={16} /> : <Sparkles size={16} />}
          sx={{ textTransform: 'none' }}
        >
          {submitting
            ? 'Submitting...'
            : taskType === 'mark-resolved'
            ? 'Mark as Resolved'
            : taskType === 'reanalyse'
            ? 'Start Reanalysis'
            : 'Assign Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
